import { Link } from 'react-router';
import type { LiveQueues } from './types.js';

/**
 * 422 (#1526, US2, design frames 1-3): "Needs attention now". Each tile opens the list holding exactly the items it
 * counts (the server counts with that list's own rule); a queue the caller may not open arrives as `null` and is not
 * shown. Yellow while something waits, orange for overdue rentals, plain at zero.
 */
type Tone = 'waiting' | 'overdue' | 'none';

// 422 v2: dark-aware tokens only (brand-yellow-soft has no dark value and stayed pale: 1.28:1 in dark mode), with the
// small text measured to WCAG AAA in dark mode (charcoal on the tints, slate on the raised card).
const TONE_CLASS: Record<Tone, string> = {
  waiting: 'border-brand-yellow bg-semantic-warning-bg dark:border-semantic-warning-text/45',
  overdue:
    'border-semantic-danger-text/40 bg-semantic-danger-bg dark:border-semantic-danger-text/45',
  none: 'border-hairline bg-canvas dark:border-hairline-strong dark:bg-surface',
};
const LABEL_CLASS: Record<Tone, string> = {
  waiting: 'text-slate dark:text-charcoal',
  overdue: 'text-slate dark:text-charcoal',
  none: 'text-slate',
};
const NOTE_CLASS: Record<Tone, string> = {
  waiting: 'text-slate dark:text-charcoal',
  overdue: 'text-slate dark:text-charcoal',
  none: 'text-steel dark:text-slate',
};
const VALUE_CLASS: Record<Tone, string> = {
  waiting: 'text-semantic-warning-text',
  overdue: 'text-semantic-danger-text',
  none: 'text-ink',
};

/** The schedule keeps its free window in Malaysia wall time (`YYYY-MM-DDTHH:mm`). */
const MYT_MS = 8 * 3600_000;
const wall = (ms: number) => new Date(ms + MYT_MS).toISOString().slice(0, 16);

interface TileSpec {
  id: string;
  label: string;
  value: number | string;
  note: string;
  to: string;
  tone: Tone;
}

function tiles(q: LiveQueues, asOf: string): TileSpec[] {
  const now = new Date(asOf).getTime();
  const waiting = (n: number): Tone => (n > 0 ? 'waiting' : 'none');
  const free = new URLSearchParams({
    free_from: wall(now),
    free_to: wall(now + 3600_000),
    customer_free: '1',
  });
  // FR-013 (design frame 4): a queue whose count could not be read says so, never 0.
  const failed = new Set(q.failed ?? []);
  const unread = (
    id: 'penalty_receipts' | 'discounts' | 'credits',
    label: string,
    to: string,
  ): TileSpec => ({
    id,
    label,
    value: '—',
    note: 'could not be loaded',
    to,
    tone: 'none',
  });
  const out: Array<TileSpec | null> = [
    q.identity === null
      ? null
      : {
          id: 'identity',
          label: 'Identity checks waiting',
          value: q.identity,
          note: q.identity > 0 ? 'submitted or resubmitted' : 'nothing waiting',
          to: '/users?identity_status=awaiting',
          tone: waiting(q.identity),
        },
    failed.has('penalty_receipts')
      ? unread(
          'penalty_receipts',
          'Penalty receipts to approve',
          '/penalties?receipt=under_review&status=active',
        )
      : q.penalty_receipts === null
        ? null
        : {
            id: 'penalty_receipts',
            label: 'Penalty receipts to approve',
            value: q.penalty_receipts,
            note: q.penalty_receipts > 0 ? 'under review' : 'nothing waiting',
            to: '/penalties?receipt=under_review&status=active',
            tone: waiting(q.penalty_receipts),
          },
    failed.has('discounts')
      ? unread('discounts', 'Discounts to approve', '/discounts?approval=pending')
      : q.discounts === null
        ? null
        : {
            id: 'discounts',
            label: 'Discounts to approve',
            value: q.discounts,
            note: q.discounts > 0 ? 'waiting for approval' : 'nothing waiting',
            to: '/discounts?approval=pending',
            tone: waiting(q.discounts),
          },
    failed.has('credits')
      ? unread('credits', 'Credits to approve', '/gift-certificates?approval=pending')
      : q.credits === null
        ? null
        : {
            id: 'credits',
            label: 'Credits to approve',
            value: q.credits,
            note: q.credits > 0 ? 'waiting for approval' : 'nothing waiting',
            to: '/gift-certificates?approval=pending',
            tone: waiting(q.credits),
          },
    {
      id: 'free_next_hour',
      label: 'Cars free for the next hour',
      value: q.free_next_hour,
      note: `of ${q.published_cars} published ${q.published_cars === 1 ? 'car' : 'cars'}`,
      to: `/schedule?${free.toString()}`,
      tone: 'none',
    },
    {
      id: 'overdue',
      label: 'Overdue rentals',
      value: q.overdue,
      note: q.overdue > 0 ? 'past their return time' : 'nothing overdue',
      to: '/bookings?bucket=overdue',
      tone: q.overdue > 0 ? 'overdue' : 'none',
    },
  ];
  return out.filter((t): t is TileSpec => t !== null);
}

export function AttentionStrip({
  queues,
  asOf,
  className = 'grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6',
}: {
  queues: LiveQueues;
  asOf: string;
  /** The grid: 6-up on the SuperAdmin page, 3-up in the Admin layout, 2-up on a phone (design frames 1-3). */
  className?: string;
}) {
  return (
    <section aria-label="Needs attention now" className="flex flex-col gap-2.5">
      <h2 className="text-[11px] font-bold tracking-wider text-steel uppercase dark:text-slate">
        Needs attention now
      </h2>
      <div className={className}>
        {tiles(queues, asOf).map((t) => (
          <Link
            key={t.id}
            to={t.to}
            data-testid={`attention-${t.id}`}
            data-tone={t.tone}
            className={`flex flex-col gap-1.5 rounded-xl border px-4 py-3.5 hover:no-underline ${TONE_CLASS[t.tone]}`}
          >
            <span className={`min-h-[30px] text-xs font-semibold ${LABEL_CLASS[t.tone]}`}>
              {t.label}
            </span>
            <span
              className={`text-[28px] leading-none font-bold tabular-nums ${VALUE_CLASS[t.tone]}`}
            >
              {t.value}
            </span>
            <span className={`text-xs ${NOTE_CLASS[t.tone]}`}>{t.note}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}
